import { useAuth } from '@/auth/Auth';
import { CustomTabs } from '@/components/shared/custom-tabs/custom-tabs';
import { usePermissions } from '@/auth/PermissionsContext';
import LayoutInside from '@/layout/LayoutInside';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

interface IntegrationsTabsSectionProps {
  children: React.ReactNode;
}

export function IntegrationsTabsSection({
  children,
}: IntegrationsTabsSectionProps) {
  const { t } = useTranslation('integration');
  const items = [{ text: t('integrations'), link: '/integrations' }];

  const { hasPermission } = usePermissions();
  const { authData } = useAuth();
  const canViewWebhookHistory = hasPermission('integrations.view_webhook_history');
  const canManageApiKeys =
    hasPermission('integrations.manage_api_keys') &&
    Boolean(authData?.public_api_enabled);

  const tabs = [
    {
      value: 'configurations',
      label: t('configurations'),
      route: '/integrations/configurations',
    },
    ...(canViewWebhookHistory ? [{
      value: 'webhooks',
      label: t('webhooks_history'),
      route: '/integrations/webhooks-history',
    }] : []),
    ...(canManageApiKeys ? [{
      value: 'developers',
      label: t('developers.title'),
      route: '/integrations/developers',
    }] : []),
  ];

  const activeTab =
    tabs.find((tab) => tab.route && location.pathname.includes(tab.route))
      ?.value || tabs[0].value;

  return (
    <LayoutInside hasBgBlur items={items}>
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className='flex flex-start mb-6'
      >
        <CustomTabs tabs={tabs} activeTab={activeTab} />
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
        {children}
      </motion.div>
    </LayoutInside>
  );
}
